export interface Instructor {
  id: string
  name: string
  role: string
  city: string
  photo: string
  bio: string
  focus: string[]
}

export interface Slide {
  title: string
  body: string
}

export interface Module {
  id: string
  title: string
  duration: string
  summary: string
  slides: Slide[]
}

export interface Course {
  id: string
  slug: string
  title: string
  subtitle: string
  category: string
  level: string
  duration: string
  sessions: number
  image: string
  instructorId: string
  excerpt: string
  description: string
  outcomes: string[]
  modules: Module[]
}

export const instructors: Instructor[] = [
  {
    id: 'sergio',
    name: 'M.C. Sergio Iván Jiménez Jiménez',
    role: 'Instructor del curso-taller',
    city: 'Ciudad de México',
    photo: 'https://static.lumi.new/c9/c976f1119e0d6a9e44e9c12a3384f30c.jpg',
    bio: 'El M.C. Sergio Iván Jiménez Jiménez imparte el curso-taller de Google Earth Engine aplicado a la ingeniería, con énfasis en índices de vegetación, calibración agrícola y evapotranspiración a partir de imágenes satelitales y datos de reanálisis.',
    focus: ['Google Earth Engine', 'Teledetección', 'Ingeniería agrícola'],
  },
]

export const courses: Course[] = [
  {
    id: 'gee-ingenieria',
    slug: 'gee-ingenieria',
    title: 'Introducción a Google Earth Engine (GEE)',
    subtitle: 'Curso-taller de la herramienta geoespacial aplicada a la ingeniería.',
    category: 'Ingeniería',
    level: 'Taller',
    duration: '12 y 19 sep 2026',
    sessions: 2,
    image: 'https://static.lumi.new/5c/5ce5584d5a407cde2d18c9342cfecd19.png',
    instructorId: 'sergio',
    excerpt: 'Taller virtual por Zoom: índices de vegetación, VICAL y evapotranspiración con GEE. Gratuito para asistentes al congreso (hasta 20 personas).',
    description:
      'Curso-taller: Introducción a la herramienta geoespacial Google Earth Engine (GEE) aplicada a la ingeniería. Fecha y hora: 12 de septiembre y 19 de septiembre de 2026, de 10:00 a 14:00, hora de la Ciudad de México. Modalidad virtual por la plataforma Zoom. Costo: gratuito para los asistentes al congreso (hasta 20 personas). Se pueden inscribir más de 20 personas, pero si requieren el diploma se debe pagar una cuota de $200 pesos mexicanos.',
    outcomes: [
      'Introducirse a Google Earth Engine (GEE)',
      'Trabajar índices de vegetación y series de tiempo (Landsat, Sentinel-2 y MODIS)',
      'Calibrar variables agrícolas con VICAL: cobertura vegetal, índice de área foliar y coeficiente de cultivo',
      'Usar datos de reanálisis en GEE para calcular evapotranspiración de referencia',
      'Estimar la evapotranspiración actual (ETa) en cultivos',
    ],
    modules: [
      {
        id: 'intro-gee',
        title: 'Introducción al Google Earth Engine (GEE)',
        duration: '90 min',
        summary: 'Fundamentos de la plataforma y su uso en ingeniería.',
        slides: [
          { title: 'Qué es GEE', body: 'Google Earth Engine es una plataforma geoespacial en la nube para procesar imágenes satelitales y series temporales a escala planetaria.' },
          { title: 'Aplicación en ingeniería', body: 'Se emplea para monitoreo ambiental, agricultura y cálculo de variables hidrológicas a partir de datos satelitales.' },
          { title: 'Entorno de trabajo', body: 'Recorrido por el editor, colecciones de imágenes y buenas prácticas para un taller introductorio.' },
        ],
      },
      {
        id: 'indices',
        title: 'Índices de vegetación y series de tiempo (Landsat, Sentinel-2 y MODIS)',
        duration: '90 min',
        summary: 'Construcción de índices y análisis temporal con tres familias de sensores.',
        slides: [
          { title: 'Landsat', body: 'Series de tiempo de media resolución para seguimiento de cobertura y cambio en el paisaje agrícola.' },
          { title: 'Sentinel-2', body: 'Mayor resolución espacial y revisita para índices de vegetación en parcelas.' },
          { title: 'MODIS', body: 'Series densas para dinámica regional y contexto climático-vegetal.' },
        ],
      },
      {
        id: 'vical',
        title: 'Calibración de variables agrícolas con VICAL',
        duration: '90 min',
        summary: 'Cobertura vegetal, índice de área foliar y coeficiente de cultivo con imágenes satelitales y GEE.',
        slides: [
          { title: 'Cobertura vegetal', body: 'Estimación de la fracción de cobertura a partir de índices espectrales.' },
          { title: 'Índice de área foliar', body: 'Relación entre señal satelital y LAI para el seguimiento del cultivo.' },
          { title: 'Coeficiente de cultivo', body: 'Calibración del Kc con VICAL para apoyar el balance hídrico.' },
        ],
      },
      {
        id: 'reanalisis',
        title: 'Datos de reanálisis en GEE para calcular evapotranspiración de referencia',
        duration: '80 min',
        summary: 'Uso de reanálisis climático para obtener ETo en el entorno de GEE.',
        slides: [
          { title: 'Reanálisis', body: 'Variables meteorológicas continuas en espacio y tiempo, disponibles como colecciones en GEE.' },
          { title: 'ETo', body: 'Cálculo de la evapotranspiración de referencia a partir de esos campos.' },
          { title: 'Práctica', body: 'Flujo de trabajo para extraer, filtrar y mapear ETo en la zona de interés.' },
        ],
      },
      {
        id: 'eta',
        title: 'Estimación de la evapotranspiración actual (ETa) en cultivos',
        duration: '80 min',
        summary: 'ETa a partir de índices de vegetación y datos de reanálisis.',
        slides: [
          { title: 'Enfoque', body: 'Combinar índices de vegetación con reanálisis para estimar el consumo real de agua del cultivo.' },
          { title: 'ETa', body: 'Interpretación de la evapotranspiración actual en el ciclo agrícola.' },
          { title: 'Cierre', body: 'Síntesis del taller y criterios para aplicar el flujo en un caso de ingeniería.' },
        ],
      },
    ],
  },
  {
    id: 'fotografia-en-pelicula',
    slug: 'fotografia-en-pelicula',
    title: 'Evapotranspiracion de cultivos usando sensores remotos',
    subtitle: 'Luz, revelado y archivo personal.',
    category: 'Imagen',
    level: 'Inicial',
    duration: '5 semanas',
    sessions: 5,
    image: 'https://static.lumi.new/8a/8a60bf8ecd2d086ab427ce02ba7c3d48.jpg',
    instructorId: 'mateo',
    excerpt: 'Aprende a exponer, revelar y editar un cuerpo de trabajo pequeño, con calma y criterio.',
    description:
      'No enseñamos a coleccionar cámaras. Enseñamos a esperar la luz, a medir y a construir un archivo que se pueda revisar dentro de diez años. Las presentaciones cubren exposición, laboratorio y edición.',
    outcomes: [
      'Exponer con fotómetro y sin él',
      'Revelar un carrete con notas de proceso',
      'Editar una serie de doce imágenes',
    ],
    modules: [
      {
        id: 'luz',
        title: 'Medir la luz',
        duration: '70 min',
        summary: 'Exposición, latitud y el hábito de anotar.',
        slides: [
          { title: 'El fotómetro', body: 'Mide la luz que importa, no la media de la escena. El sujeto manda.' },
          { title: 'Latitud', body: 'La película perdona de un modo distinto al sensor. Aprende dónde se rompe.' },
          { title: 'Cuaderno', body: 'Anota hora, película, revelado. El archivo empieza aquí.' },
        ],
      },
      {
        id: 'laboratorio',
        title: 'Revelado',
        duration: '80 min',
        summary: 'Química, tiempos y consistencia.',
        slides: [
          { title: 'Preparar', body: 'Temperatura estable, bidón limpio, temporizador a la vista.' },
          { title: 'Agitación', body: 'Un gesto repetible vale más que una receta copiada.' },
          { title: 'Secado', body: 'El polvo es parte del oficio. Trabaja con calma y un espacio limpio.' },
        ],
      },
      {
        id: 'serie',
        title: 'Editar la serie',
        duration: '55 min',
        summary: 'De treinta negativos a doce imágenes.',
        slides: [
          { title: 'Contactos', body: 'Mira el conjunto antes que la foto suelta. La serie tiene un pulso.' },
          { title: 'Descartes', body: 'Quitar es el trabajo. Lo que queda debe poder colgarse en una pared.' },
          { title: 'Orden', body: 'El orden cuenta una historia. Prueba tres secuencias distintas.' },
        ],
      },
    ],
  },
  {
    id: 'direccion-de-arte',
    slug: 'direccion-de-arte',
    title: 'Productividad del agua usando sensores remotos',
    subtitle: 'Composición, materiales y criterio visual.',
    category: 'Identidad',
    level: 'Avanzado',
    duration: '8 semanas',
    sessions: 8,
    image: 'https://static.lumi.new/material/51/5165d314a284d5d1091e6ac80c2a9589.png',
    instructorId: 'helena',
    excerpt: 'Un taller para dirigir imágenes y materiales con una paleta corta y decisiones firmes.',
    description:
      'La dirección de arte no es decorar. Es elegir qué se ve, qué se toca y qué se omite. Trabajamos con referencias, maquetas y presentaciones de sesión para construir un sistema visual sobrio.',
    outcomes: [
      'Definir una paleta y un sistema de materiales',
      'Dirigir una sesión fotográfica breve',
      'Presentar un artbook de 8 páginas',
    ],
    modules: [
      {
        id: 'paleta',
        title: 'Paleta y materiales',
        duration: '65 min',
        summary: 'Color, papel y límites.',
        slides: [
          { title: 'Pocos colores', body: 'Tres colores y un acento. Si hace falta el cuarto, el sistema no está listo.' },
          { title: 'Tacto', body: 'El papel habla. Elige gramaje y textura antes que efectos digitales.' },
          { title: 'Referencias', body: 'Un tablero corto. Diez imágenes, no cien.' },
        ],
      },
      {
        id: 'sesion',
        title: 'Dirigir la sesión',
        duration: '75 min',
        summary: 'Brief, ritmo y presencia en el set.',
        slides: [
          { title: 'Brief', body: 'Una página: intención, objetos, luz, lo que no se fotografiará.' },
          { title: 'Ritmo', body: 'Menos tomas, más atención. El cansancio se ve.' },
          { title: 'Cierre', body: 'Elige tres imágenes en el momento. El resto espera al laboratorio.' },
        ],
      },
      {
        id: 'artbook',
        title: 'El artbook',
        duration: '60 min',
        summary: 'Paginación, silencios y entrega.',
        slides: [
          { title: 'Página', body: 'El margen es contenido. No lo llenes por miedo al vacío.' },
          { title: 'Secuencia', body: 'Alterna densidades. Una imagen grande pide una página quieta después.' },
          { title: 'Entrega', body: 'PDF para archivo y un cuadernillo impreso para la mesa.' },
        ],
      },
    ],
  },
  {
    id: 'liderazgo-en-estudio',
    slug: 'liderazgo-en-estudio',
    title: 'Sistemas de información geografica usando QGIS',
    subtitle: 'Reuniones, ritmo y cuidado del equipo.',
    category: 'Oficio',
    level: 'Intermedio',
    duration: '4 semanas',
    sessions: 4,
    image: 'https://static.lumi.new/82/827978dc2362752dc332cdefaf519815.png',
    instructorId: 'ines',
    excerpt: 'Herramientas discretas para dirigir un estudio sin teatralidad ni agotamiento.',
    description:
      'Un curso para quienes ya tienen un equipo y quieren trabajar con más claridad. Cada módulo incluye una presentación y un ejercicio para la semana siguiente: agendas, feedback y cierre de proyectos.',
    outcomes: [
      'Diseñar una reunión de 25 minutos',
      'Dar feedback sin diluir el criterio',
      'Cerrar un proyecto con una nota de aprendizaje',
    ],
    modules: [
      {
        id: 'reunion',
        title: 'La reunión corta',
        duration: '45 min',
        summary: 'Agenda, turnos y acta mínima.',
        slides: [
          { title: 'Antes', body: 'Tres puntos, un dueño por punto, hora de cierre visible.' },
          { title: 'Durante', body: 'Quien convoca facilita. Quien toma notas no dirige.' },
          { title: 'Después', body: 'Un párrafo de acuerdos el mismo día. Nada más.' },
        ],
      },
      {
        id: 'feedback',
        title: 'Feedback con pulso',
        duration: '50 min',
        summary: 'Observación, efecto y petición.',
        slides: [
          { title: 'Hecho', body: 'Describe lo ocurrido sin adjetivo. El adjetivo llega después, si hace falta.' },
          { title: 'Efecto', body: 'Explica qué cambió en el trabajo o en el equipo.' },
          { title: 'Petición', body: 'Una petición concreta para la próxima entrega.' },
        ],
      },
      {
        id: 'cierre',
        title: 'Cerrar el encargo',
        duration: '40 min',
        summary: 'Archivo, gratitud y siguiente ciclo.',
        slides: [
          { title: 'Archivo', body: 'Guarda decisiones, no solo archivos. El porqué se olvida primero.' },
          { title: 'Nota', body: 'Media página: qué repetir, qué no volver a intentar.' },
          { title: 'Descanso', body: 'El estudio también necesita silencio entre encargos.' },
        ],
      },
    ],
  },
]

export const heroImage = 'https://static.lumi.new/material/48/48161d73ccd0288056d9479b7ec28809.png'

export function getInstructor(id: string) {
  return instructors.find((item) => item.id === id)
}

export function getCourse(slug: string) {
  return courses.find((item) => item.slug === slug)
}

export function searchCourses(query: string) {
  const q = query.trim().toLowerCase()
  if (!q) return courses
  return courses.filter((course) => {
    const instructor = getInstructor(course.instructorId)
    const haystack = [
      course.title,
      course.subtitle,
      course.category,
      course.excerpt,
      course.level,
      instructor?.name ?? '',
    ]
      .join(' ')
      .toLowerCase()
    return haystack.includes(q)
  })
}

export const categories = ['Todos', ...Array.from(new Set(courses.map((course) => course.category)))]
